import {
  Box,
  Image,
  Modal,
  ModalContent,
  ModalOverlay,
  Text
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useSearchParams } from 'react-router-dom';

import container1 from '../images/container1.png';
import container2 from '../images/container2.png';
import closeIcon from '../images/cross.png';
import AnnouncementContent from './AnnouncementContent';
import MaintenanceContent from './MaintenanceContent';

const DISPLAY_CONTENT = {
  ANNOUNCEMENT: 'announcement',
  MAINTENANCE: 'maintenance'
};

/**
 * @description: 公告與維護通知的 Modal 組件，根據 URL 參數控制顯示
 * @return {JSX.Element}
 */
const AnnouncementModal = () => {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const openModalParams = searchParams.get('announcement_open');
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(DISPLAY_CONTENT.ANNOUNCEMENT);
  const isHandleAllGame = useSelector((state) => state.isHandleAllGame);
  const reduxAnnouncementList = useSelector(
    (state) => state.isAnnouncementList
  );
  const { allGamePlatform = [] } = isHandleAllGame;

  // 從 allGamePlatform 中篩選出有維護時間的遊戲平台
  const maintenanceList = allGamePlatform.filter((item) => {
    const isOpenStart =
      item.maintenanceStartTime && item.maintenanceStartTime !== '00:00:00';
    const isOpenEnd =
      item.maintenanceEndTime && item.maintenanceEndTime !== '00:00:00';
    return isOpenStart || isOpenEnd;
  });

  const handleCloseModal = () => setModalOpen(false);

  // 切換公告與維護通知的內容
  const handleTabClick = () => {
    if (activeTab === DISPLAY_CONTENT.ANNOUNCEMENT) {
      setActiveTab(DISPLAY_CONTENT.MAINTENANCE);
    } else {
      setActiveTab(DISPLAY_CONTENT.ANNOUNCEMENT);
    }
  };

  // 根據 URL 參數控制 Modal 的顯示
  useEffect(() => {
    if (openModalParams === 'true') {
      setModalOpen(true);
      const currentParams = new URLSearchParams(location.search);
      currentParams.delete('announcement_open');
      setSearchParams(currentParams, { replace: true });
    }
  }, [openModalParams, location.search, setSearchParams]);

  //  當初始化取得公告資料時，自動彈出彈窗（每次會話只彈出一次）
  useEffect(() => {
    // 檢查是否已經顯示過公告（使用 sessionStorage，刷新頁面後會重置）
    const hasShownAnnouncement = sessionStorage.getItem('hasShownAnnouncement');

    // 如果有公告數據且尚未顯示過，自動打開彈窗
    if (reduxAnnouncementList.list.length > 0 && !hasShownAnnouncement) {
      setModalOpen(true);
      // 標記已經顯示過公告
      sessionStorage.setItem('hasShownAnnouncement', 'true');
    }
  }, [reduxAnnouncementList.list.length]);

  useEffect(() => {
    if (reduxAnnouncementList.list.length > 0) {
      setActiveTab(DISPLAY_CONTENT.ANNOUNCEMENT);
    } else {
      setActiveTab(DISPLAY_CONTENT.MAINTENANCE);
    }
  }, [reduxAnnouncementList.list.length]);

  return (
    <Modal isOpen={modalOpen} isCentered>
      <ModalOverlay />
      <ModalContent
        position={'relative'}
        background={'transparent'}
        bgImage={
          activeTab === DISPLAY_CONTENT.ANNOUNCEMENT ? container1 : container2
        }
        bgSize={'100% 100%'}
        height={{ base: '450px', sm: '600px' }}
        maxW={{ base: '100%', sm: '500px' }}
        padding={{ base: '5em 2em 1em 2em', sm: '6.5em 2.5em 2em 3em' }}
        marginX={'1em'}
        shadow={'none'}
      >
        {/* 關閉按鈕 */}
        <Box
          onClick={handleCloseModal}
          cursor={'pointer'}
          height={{ base: '3em', sm: '4em' }}
          width={{ base: '3em', sm: '4em' }}
          position={'absolute'}
          top={'-0.3em'}
          right={'-1em'}
          _hover={{ transform: 'scale(1.1)', transition: 'transform 0.2s' }}
        >
          <Image
            src={closeIcon}
            alt="Close"
            cursor="pointer"
            width={'100%'}
            height={'100%'}
            objectFit="cover"
          />
        </Box>

        <Box
          position={'absolute'}
          top={{ base: '1em', sm: '2em' }}
          left={{ base: '1em', sm: '3em' }}
          cursor={
            activeTab !== DISPLAY_CONTENT.ANNOUNCEMENT ? 'pointer' : 'default'
          }
          _hover={
            activeTab !== DISPLAY_CONTENT.ANNOUNCEMENT
              ? { transform: 'scale(1.1)', transition: 'transform 0.2s' }
              : {}
          }
          onClick={
            activeTab !== DISPLAY_CONTENT.ANNOUNCEMENT
              ? handleTabClick
              : undefined
          }
          alignItems={'center'}
          justifyContent={'center'}
          display={'flex'}
          width={{ base: '10em', sm: '9em' }}
        >
          <Text fontWeight={'bold'} fontSize={{ base: '1.5rem', sm: '1.5rem' }}>
            {t(`announcement.${DISPLAY_CONTENT.ANNOUNCEMENT}`)}
          </Text>
        </Box>

        <Box
          position={'absolute'}
          top={{ base: '1em', sm: '2em' }}
          right={{ base: '4em', sm: '5em' }}
          cursor={
            activeTab !== DISPLAY_CONTENT.MAINTENANCE ? 'pointer' : 'default'
          }
          _hover={
            activeTab !== DISPLAY_CONTENT.MAINTENANCE
              ? { transform: 'scale(1.1)', transition: 'transform 0.2s' }
              : {}
          }
          onClick={
            activeTab !== DISPLAY_CONTENT.MAINTENANCE
              ? handleTabClick
              : undefined
          }
          width={{ base: '8rem', sm: '10rem' }}
          display={'flex'}
          alignItems={'center'}
          justifyContent={'center'}
        >
          <Text fontWeight={'bold'} fontSize={{ base: '1.5em', sm: '1.5em' }}>
            {t(`announcement.${DISPLAY_CONTENT.MAINTENANCE}`)}
          </Text>
        </Box>

        <Box height={'100%'} width={'100%'} overflow={'hidden'}>
          {/* 根據 activeTab 顯示不同的內容 */}
          {activeTab === DISPLAY_CONTENT.ANNOUNCEMENT ? (
            <AnnouncementContent
              reduxAnnouncementList={reduxAnnouncementList}
            />
          ) : (
            <MaintenanceContent maintenanceList={maintenanceList} />
          )}
        </Box>
      </ModalContent>
    </Modal>
  );
};
export default AnnouncementModal;
